<template>
  <v-data-table
    v-model="selected"
    v-model:sort-by="sortBy"
    :disable-sort="props.disableSort ?? false"
    :class="[
      `e-data-table--vertical-align-${props.verticalAlign}`,
      {
        interactive,
        'content-wrap': contentWrap,
        'e-data-table--small': small,
        'e-data-table--styled-header': styledHeader,
      },
    ]"
    :items="items"
    :headers="shownHeaders as VDataTable['$props']['headers']"
    :row-props="rowProps"
    :show-select="showSelect && !!modelValue"
    :item-value="itemValue"
    :return-object="returnObject"
    hide-default-footer
    initial-sort-order="asc"
    class="e-data-table"
    items-per-page="-1"
    sticky
    @click:row="onRowClick"
  >
    <template v-if="loading" #body>
      <tr v-for="i in loadingRows" :key="i" class="e-data-table__loading-row">
        <td v-for="j in loadingCollCount" :key="j">
          <skeleton-loader />
        </td>
      </tr>
    </template>

    <template v-for="(_, slotName) in slots" v-slot:[slotName]="slotData">
      <slot :name="slotName" v-bind="slotData" />
    </template>

    <template v-for="(header, index) in shownHeaders" v-slot:[`header.${header.key}`]="props" :key="index">
      <div
        class="e-data-table__header-item"
        :class="{ 'non-sortable': !props.column.sortable }"
        @click="props.column.sortable ? props.toggleSort(props.column) : null"
      >
        <slot :name="`header.${header.key}`" v-bind="props">
          <div class="th ellipsis-1">
            {{ props.column.title }}
          </div>
        </slot>

        <e-icon
          v-if="props.column.sortable"
          name="sorting"
          class="e-data-table__sort-icon"
          :class="{
            active: props.sortBy[0]?.key === header.key,
            asc: props.sortBy[0]?.order === 'asc' && props.sortBy[0]?.key === header.key,
          }"
          :size="16"
        />
      </div>
    </template>

    <template v-slot:[`header.data-table-select`]="props">
      <e-checkbox
        :model-value="props.allSelected"
        :square-selected="props.someSelected"
        :disabled="loading"
        @update:modelValue="props.selectAll"
      />
    </template>
    <template v-slot:[`item.data-table-select`]="{ isSelected, item, internalItem, toggleSelect }">
      <e-checkbox
        :model-value="isSelected(internalItem)"
        :disabled="!!item.disabled"
        @click.stop
        @update:modelValue="toggleSelect(internalItem)"
      />
    </template>
  </v-data-table>
</template>

<script setup lang="ts" generic="T extends Record<string, unknown>, K extends Paths<T> | undefined">
import { isArray } from '@apollo/client/utilities';
import { useDebounceFn } from '@vueuse/core';
import { computed, onBeforeUpdate, ref, toRef } from 'vue';
import { useDisplay } from 'vuetify';
import { type SortItem } from 'vuetify/lib/components/VDataTable/composables/sort.mjs';
import type { EDataItemsOnRowClickItem } from '@/core/types/components/components';
import type { Paths } from '@/core/types/gengeric';
import type { VDataTable } from 'vuetify/components';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';
import { ECheckbox, EIcon } from '@/core/components/ui';
import { type DataTableHeaders, type RowProps, type TableSlots } from '@/core/types/components/table';
import { deepCloneObject } from '@/core/utils/object';
import { SortOrders } from '@/graphql/generated';

const props = withDefaults(
  defineProps<{
    items: T[];
    headers: DataTableHeaders;
    interactive?: boolean;
    stickyHeaderTop?: string;
    contentWrap?: boolean;
    rowProps?: RowProps<T>;
    itemValue?: K;
    returnObject?: boolean;
    small?: boolean;
    styledHeader?: boolean;
    verticalAlign?: 'top' | 'middle' | 'bottom';
    loading?: boolean;
    loadingRows?: number;
    sortBy?: SortBy;
    showSelect?: boolean;
    showSelectColumn?: boolean;
    disableSort?: boolean;
  }>(),
  {
    stickyHeaderTop: '0px',
    verticalAlign: 'middle',
    loadingRows: 10,
    showSelect: true,
    showSelectColumn: true,
  },
);

type SortBy = {
  key: string | null;
  order: SortOrders;
};

const emit = defineEmits<{
  (event: 'onRowClick', e: Event, row: EDataItemsOnRowClickItem<T>): void;
  (event: 'update:sortBy', value: SortBy | undefined): void;
}>();

const $display = useDisplay();

const selected = defineModel<K extends Paths<T> ? T[K][] : T[]>();

// v-data-table calls @update:sort-by twice when sorting, so we need to debounce the call
const emitUpdateDebounce = useDebounceFn((value: SortItem[]) => {
  emit(
    'update:sortBy',
    value?.[0]
      ? { key: value[0].key, order: value[0].order === 'asc' ? SortOrders.Asc : SortOrders.Desc }
      : { key: null, order: SortOrders.Desc },
  );
}, 10);

const sortBy = props.sortBy
  ? computed({
      get: () => [
        {
          key: props.sortBy!.key ?? '',
          order: props.sortBy!.order === SortOrders.Asc ? 'asc' : ('desc' as 'asc' | 'desc'),
        },
      ],
      set: (value) => {
        void emitUpdateDebounce(value);
      },
    })
  : ref(undefined);

const onRowClick = (e: Event, row: EDataItemsOnRowClickItem<T>) => {
  emit('onRowClick', e, row);
};

const slots = defineSlots<TableSlots<T>>();

const shownHeaders = ref<DataTableHeaders>(props.headers);

const onNodeUpdate = () => {
  let headersCopy = props.headers;

  if (!shownHeaders.value || !isArray(shownHeaders.value)) {
    shownHeaders.value = headersCopy;
    return;
  }

  if (slots['item.dataItemSecondRow'] && !headersCopy.some((h) => h.key === 'dataItemSecondRow')) {
    headersCopy.push({
      key: 'dataItemSecondRow',
      sortable: false,
      width: 0,
      cellProps: {
        class: 'absolute',
      },
      headerProps: {
        style: 'padding: 0',
      },
    });
  } else if (!slots['item.dataItemSecondRow']) {
    headersCopy = headersCopy.filter((h) => h.key !== 'dataItemSecondRow');
  }

  if (props.showSelectColumn && slots['item.data-table-select'] && !headersCopy.some((h) => h.key === 'data-table-select')) {
    headersCopy.unshift({
      key: 'data-table-select',
      sortable: false,
      width: $display.mdAndDown.value ? 38 : 48,
    });
  }

  shownHeaders.value = deepCloneObject(headersCopy);
};

const loadingCollCount = toRef(() => {
  if (selected.value) {
    return shownHeaders.value.length + 1;
  }
  return shownHeaders.value.length;
});

onBeforeUpdate(onNodeUpdate);
onNodeUpdate();
</script>

<style scoped lang="scss">
$parent: v-data-table;

.e-data-table {
  --v-table-header-height: 48px;
  --v-table-row-height: 76px;

  color: $text-secondary-color;
  white-space: nowrap;

  @include md-and-down {
    --v-table-header-height: 40px;
    --v-table-row-height: 64px;
  }

  :deep(.v-data-table__tr) {
    &.disable-loader {
      position: relative;

      pointer-events: none;

      td {
        position: static !important;

        overflow: initial !important;

        &:nth-of-type(2) {
          @include disable-loader;
        }
      }
    }
    &:has(.absolute:not(:empty)) {
      --v-table-row-height: 104px;

      .v-data-table__td {
        padding-bottom: 32px;

        &.absolute {
          bottom: 16px;
        }
      }
    }
  }

  &:not(&.interactive) {
    :deep(.v-data-table__tr--clickable) {
      cursor: auto;
    }
  }

  &.interactive {
    :deep(.v-data-table__tr--clickable:not(.inactive)) {
      @include hover {
        color: $text-primary-color;

        box-shadow: $border-box-shadow;
      }
    }
  }

  &__header-item {
    display: flex;
    align-items: center;
    
    color: $text-ordinary-color;

    @include hover {
      color: $text-primary-color;

      .e-data-table__sort-icon {
        color: $text-primary-color;
      }

      &.non-sortable {
        color: $text-ordinary-color;
      }
    }
    
    &:has(.e-data-table__sort-icon.active) {
      color: $text-primary-color;
    }
  }

  &__sort-icon {
    margin-left: 8px;

    color: $text-ordinary-color;

    &.active {
      color: $text-primary-color;
    }

    &.asc {
      transform: scaleY(-1);
    }

    &:hover {
      color: $text-primary-color;
    }
  }

  :not(&.content-wrap) {
    :deep(.v-table__wrapper) {
      .v-data-table__td:not(.v-data-table__th) {
        @include ellipsis();

        & > div {
          @include ellipsis();
        }
      }
    }
  }

  &.content-wrap {
    :deep(.v-table__wrapper) {
      .v-data-table__td:not(.v-data-table__th) {
        white-space: wrap;
        white-space: normal;
        word-break: break-word;
      }
    }
  }

  :deep(.v-table__wrapper) {
    overflow: initial;

    border-radius: 0;

    .inactive {
      color: $text-inactive-color;

      pointer-events: none;

      [data-table-clickable] {
        pointer-events: all;
      }
    }

    .v-data-table__th--sticky {
      z-index: 9;
    }

    table {
      // Убрал для того, чтобы на странице сверки при селекте не раздувало первую колонку
      // table-layout: fixed;

      tr {
        th,
        td {
          padding: 12px;
          &.v-data-table-column--no-padding {
            padding: 12px;
          }

          @include md-and-down {
            padding: 8px;
            &.v-data-table-column--no-padding {
              padding: 8px;
            }
          }
        }
      }
    }

    .#{$parent}__th--sticky {
      top: v-bind(stickyHeaderTop) !important;
    }

    .#{$parent}-header {
      &__content {
        font-size: 12px;
        line-height: 1.2;
        font-weight: 500;
        color: $text-ordinary-color;

        span {
          @include ellipsis();
        }

        @include md-and-down {
          font-size: 11px;
        }
      }

      &__sort-icon {
        mask: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3.33203 4.66602H12.6654' stroke='%23131316' stroke-width='1.5' stroke-linecap='round'/%3E%3Cpath d='M3.33203 8H9.9987' stroke='%23131316' stroke-width='1.5' stroke-linecap='round'/%3E%3Cpath d='M3.33203 11.334H6.66536' stroke='%23131316' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E%0A");
        mask-position: center;

        margin-left: 7px;

        background-color: currentColor;

        opacity: 1 !important;

        &:before {
          display: none;
        }
      }
    }

    .#{$parent}-column {
      .#{$parent}-header__content {
        flex-direction: row;
      }

      &--align-start {
        .e-data-table__header-item {
          justify-self: start;

          float: left;
        }
      }

      &--align-center {
        .e-data-table__header-item {
          justify-self: center;

          width: 50%;
          margin: 0 auto;
        }
      }

      &--align-end {
        .e-data-table__header-item {
          justify-self: end;

          float: right;
        }
      }
    }

    .#{$parent}__th,
    .#{$parent}__td {
      border: none !important;
      box-shadow: none !important;
    }

    .#{$parent}__td {
      font-size: 14px;
      font-weight: 600;

      @include md-and-down {
        font-size: 12px;
      }
    }

    .#{$parent}__th {
      @include hover {
        .#{$parent}-header {
          &__content {
            span {
              color: $text-primary-color !important;
            }
          }
        }
      }

      &--sorted {
        .#{$parent} {
          &-header {
            &__content {
              color: $text-secondary-color;
            }

            &__sort-icon {
              background-color: $text-primary-color;

              transform: scaleY(-1);

              &.mdi-arrow-down {
                transform: scaleY(1);
              }
            }
          }
        }
      }
    }

    .#{$parent}__tr {
      position: relative;

      border-radius: $border-radius;

      transition: box-shadow $transition-duration;

      &:nth-of-type(2n + 2) {
        position: relative;
        z-index: 1;

        td:first-child {
          &:before {
            content: '';
            position: absolute;
            top: 1px;
            right: 1px;
            bottom: 1px;
            left: 1px;
            z-index: -1;

            display: block;

            background-color: $bg-light-color;
            border-radius: $border-radius;
          }
        }
      }
    }

    .#{$parent}__td {
      &.absolute {
        position: absolute;
        bottom: 6px;
        left: 0;

        display: block;

        width: 100% !important;
        height: auto;
        padding: 0 0 0 8px !important;

        div {
          padding: 0;
        }
      }
    }
  }

  &--small {
    --v-table-header-height: 40px;
    --v-table-row-height: 48px;

    @include sm-and-down {
      --v-table-header-height: 40px;
      --v-table-row-height: 44px;
    }

    :deep(.v-table__wrapper) {
      & > table > thead > tr > th {
        padding: 0 8px !important;
      }

      & > table > tbody > tr > td {
        padding: 8px;
      }

      .v-data-table-header__sort-icon {
        margin-left: 4px;
      }

      .v-data-table__td {
        font-size: 12px;
        line-height: 16px;
        font-weight: 500;
      }
    }
  }

  &--styled-header {
    --v-table-header-height: 76px;
    :deep(thead) {
      tr {
        th {
          background-color: $primary-light-color !important;
          box-shadow: none !important;

          &:first-child {
            border-top-left-radius: $border-radius;
            border-bottom-left-radius: $border-radius;
          }

          &:last-child {
            border-top-right-radius: $border-radius;
            border-bottom-right-radius: $border-radius;
          }
        }
      }
    }
  }

  &--vertical-align-top {
    :deep(.v-table__wrapper > table > tbody > tr > td) {
      vertical-align: top;
    }
  }

  &--vertical-align-middle {
    :deep(.v-table__wrapper > table > tbody > tr > td) {
      vertical-align: middle;
    }
  }

  &--vertical-align-bottom {
    :deep(.v-table__wrapper > table > tbody > tr > td) {
      vertical-align: bottom;
    }
  }

  .e-data-table__loading-row {
    td {
      border-bottom: none !important;
    }
  }
}
</style>
